import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, Save } from 'lucide-react';

import { useHaptics } from '@/hooks/useHaptics';
import {
  usePopularDecksQuery,
  useSaveDeckMutation,
} from '@/services/qbotApi';
import { DeckGrid } from '@/components/cr/DeckGrid';
import { cn } from '@/lib/cn';
import type { PopularDeck } from '@/types/qbot';

const ARCHETYPES = ['', 'Beatdown', 'Control', 'Cycle', 'Siege', 'Bait', 'Bridge Spam'];

/**
 * Meta deck browser — Top 200 ladder aggregations from QBot's `DeckSnapshot`
 * table. Archetype filter is a horizontal chip rail; cards filter is
 * intentionally left out of the MVP (requires the card-name picker which
 * is a bigger UI chunk; ship after the picker lands).
 *
 * "Save" copies the deck into the user's saved list, archetype detected
 * server-side.
 */
export function CRPopularDecks() {
  const { t } = useTranslation('cr');
  const haptics = useHaptics();
  const [archetype, setArchetype] = useState<string>('');
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const { data, isFetching, error } = usePopularDecksQuery({
    archetype: archetype || undefined,
    page,
    pageSize,
  });
  const [saveDeck, { isLoading: saving }] = useSaveDeckMutation();

  return (
    <div className="flex h-full flex-col gap-3 overflow-y-auto px-4 py-4">
      <header className="flex flex-col gap-2">
        <h1 className="text-xl font-bold text-fg">{t('popular.title')}</h1>
        <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {ARCHETYPES.map((a) => (
            <button
              key={a || 'all'}
              type="button"
              onClick={() => {
                haptics.selection();
                setArchetype(a);
                setPage(1);
              }}
              className={cn(
                'shrink-0 rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-wide transition-colors',
                archetype === a
                  ? 'border-brand bg-brand text-brand-fg'
                  : 'border-border bg-bg-secondary text-fg-muted',
              )}
            >
              {a || t('popular.filter.all')}
            </button>
          ))}
        </div>
      </header>

      {!!error && (
        <p className="rounded-md bg-rose-500/10 p-3 text-sm text-rose-300">
          {t('popular.error')}
        </p>
      )}

      <ul className="flex flex-col gap-3">
        {data?.decks.map((deck, idx) => (
          <PopularDeckCard
            key={`${deck.cardIds.join(',')}-${idx}`}
            deck={deck}
            saving={saving}
            onSave={async (name) => {
              try {
                await saveDeck({ name, cards: deck.cards.map((c) => c.name) }).unwrap();
                haptics.notification('success');
              } catch {
                haptics.notification('error');
              }
            }}
          />
        ))}
      </ul>

      {data && (
        <nav className="mt-auto flex items-center justify-between rounded-lg bg-bg-secondary p-2">
          <button
            type="button"
            disabled={page <= 1 || isFetching}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="flex h-8 w-8 items-center justify-center rounded-md text-fg disabled:opacity-30"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <span className="text-xs text-fg-muted tabular-nums">
            {t('popular.page', { page, total: data.pagination.totalPages || 1 })}
          </span>
          <button
            type="button"
            disabled={page >= (data.pagination.totalPages || 1) || isFetching}
            onClick={() => setPage((p) => p + 1)}
            className="flex h-8 w-8 items-center justify-center rounded-md text-fg disabled:opacity-30"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </nav>
      )}
    </div>
  );
}

interface PopularDeckCardProps {
  deck: PopularDeck;
  saving: boolean;
  onSave: (name: string) => void;
}

function PopularDeckCard({ deck, saving, onSave }: PopularDeckCardProps) {
  const { t } = useTranslation('cr');
  return (
    <li className="rounded-xl bg-bg-secondary p-3">
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-fg">{deck.archetype}</p>
          <p className="text-[11px] text-fg-muted">
            {t('popular.usage', { count: deck.count })}
            {deck.winRate ? ` · ${Math.round(deck.winRate * 100)}% WR` : ''}
          </p>
        </div>
        <button
          type="button"
          disabled={saving}
          onClick={() => onSave(`${deck.archetype} top deck`)}
          className="flex items-center gap-1 rounded-md bg-bg-tertiary px-2.5 py-1 text-[11px] font-semibold text-brand disabled:opacity-50"
        >
          <Save className="h-3.5 w-3.5" />
          {t('popular.save')}
        </button>
      </div>
      <DeckGrid cards={deck.cards} avgElixir={deck.avgElixir} showLevels={false} />
    </li>
  );
}
